import React from 'react';
import {useNavigation} from '@react-navigation/native';
import {
  calculateWithdrawReceived,
  resolveMaxWithdrawAmount,
  resolveEffectiveWithdrawInfo,
  useWithdrawSessionStore,
  useWithdrawStore as useProgressiveWithdrawStore,
  validateWithdrawSubmit,
  WithdrawWebHomePage,
  type WithdrawPayType,
} from '@fe-progressive/package-progressive/withdraw';
import {showProgressiveHostToast} from '@fe-progressive/package-progressive/progressive-toast';
import {goBack, goCS, goTo, inApp, toPriceStr} from '@/utils';
import globalStore from '@/services/global.state';
import theme from '@/style';
import {IUserInfo, postUserInfo} from '@/services/global.service';
import {
  CryptoCoin,
  getCryptoCoinList,
  getPayConfig,
  PayConfig,
  postCryptoWithdrawal,
} from '@/services/pay.service';
import {BasicObject} from '@/types';
import {useTranslation} from 'react-i18next';
import useWithdrawAmountOptions from '../withdraw-new/hooks/useWithdrawAmountOptions';
import useHostWithdrawStore from '../withdraw-new/withdraw.store';
import {
  CardListItemType,
  getBankList,
  getWithdrawInfo,
  onWithdraw,
} from './withdraw-service';
import {useModalManager} from '@basicComponents/sequential-modal/ModalManagerProvider';
import {getRewardStatus} from '@/pages/home/home.service';
import ToWithdraw, {
  TO_WITH_DRAW,
} from '@businessComponents/down-app-modal/to-withdraw';
import Rewarded, {
  DOWNLOAD_REWARDED,
} from '@businessComponents/down-app-modal/rewarded';
import {useWithdrawProgressiveViewportHeightStyle} from './withdraw-progressive-theme';

const WITHDRAW_SUBMIT_MESSAGE_KEY = {
  needLogin: '',
  noWithdrawable: 'withdraw-page.error.noWithdrawable',
  addAmount: 'withdraw-page.error.addAmount',
  addCard: 'withdraw-page.error.addCard',
  selectCoin: 'withdraw-page.error.selectCoin',
  addAddress: 'withdraw-page.error.addAddress',
  invalidAddress: 'withdraw-page.error.invalidAddress',
  minAmount: 'withdraw-page.error.minAmount',
  maxAmount: 'withdraw-page.error.maxAmount',
} as const;

const Withdraw = () => {
  const {i18n} = useTranslation();
  const navigation = useNavigation();
  const viewportHeightStyle = useWithdrawProgressiveViewportHeightStyle();
  const [loading, setLoading] = React.useState(false);
  const [cardList, setCardList] = React.useState<CardListItemType[]>([]);
  const [showCard, setShowCard] = React.useState(false);
  const [price, setPrice] = React.useState('');
  const [selectedCard, setSelectedCard] = React.useState<string | undefined>();
  const [user, setUser] = React.useState<IUserInfo | BasicObject>({});
  const [payType, setPayType] = React.useState<WithdrawPayType>('UPI');
  const [cryptoCoinList, setCryptoCoinList] = React.useState<CryptoCoin[]>([]);
  const [selectedCoin, setSelectedCoin] = React.useState<CryptoCoin | null>(
    null,
  );
  const [payConfig, setPayConfig] = React.useState<PayConfig | null>(null);
  const [recipientAddress, setRecipientAddress] = React.useState('');
  const [showCoinSelector, setShowCoinSelector] = React.useState(false);
  const setHostWithdrawInfo = useHostWithdrawStore(s => s.setWithdrawInfo);
  const withdrawInfo = useHostWithdrawStore(s => s.info);
  const selectedAmount = useHostWithdrawStore(s => s.selectedAmount);
  const progressiveWithdraw = useProgressiveWithdrawStore;
  const progressiveSession = useWithdrawSessionStore;
  const {showModal} = useModalManager();
  const {options: rawAmountOptions, loading: amountOptionsLoading} =
    useWithdrawAmountOptions(payType === 'CRYPTO');
  const effectiveWithdrawInfo = React.useMemo(
    () => resolveEffectiveWithdrawInfo(withdrawInfo, user),
    [user, withdrawInfo],
  );

  React.useEffect(() => {
    progressiveSession.getState().syncSession({
      isLoggedIn: Boolean(globalStore.token),
      tokenReady: true,
      userInfo: Object.keys(user).length > 0 ? user : null,
    });
  }, [progressiveSession, user]);

  React.useEffect(() => {
    if (selectedAmount === 0) {
      setPrice(
        resolveMaxWithdrawAmount(
          effectiveWithdrawInfo?.withdrawableBalance || 0,
          payConfig?.cryptoWithdrawalAmountLimit,
        ).toString(),
      );
    } else if (selectedAmount) {
      setPrice(selectedAmount.toString());
    }
  }, [
    payConfig?.cryptoWithdrawalAmountLimit,
    selectedAmount,
    effectiveWithdrawInfo?.withdrawableBalance,
  ]);

  React.useEffect(() => {
    setLoading(true);
    const promises: Promise<any>[] = [
      getUserInfo(),
      onGetBankList(),
      getWithDrawInfo(),
    ];
    if (payType === 'CRYPTO') {
      promises.push(fetchCryptoData());
    }
    Promise.allSettled(promises).finally(() => setLoading(false));
    const unsubscribe = navigation.addListener('focus', () => {
      onGetBankList();
    });
    return unsubscribe;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [navigation, payType]);

  const fetchCryptoData = async () => {
    try {
      const [coins, config] = await Promise.all([
        getCryptoCoinList(),
        getPayConfig(),
      ]);
      setCryptoCoinList(coins);
      if (coins.length > 0 && !selectedCoin) {
        setSelectedCoin(coins[0]);
      }
      setPayConfig(config);
    } catch (error) {
      console.error('Failed to fetch crypto data:', error);
    }
  };

  const getUserInfo = async () => {
    const res = await postUserInfo();
    setUser(res);
    progressiveSession.getState().syncSession({
      isLoggedIn: Boolean(globalStore.token),
      tokenReady: true,
      userInfo: res,
    });
  };

  const onGetBankList = async () => {
    const res = (await getBankList()) || [];
    if (res.length > 0 && !selectedCard) {
      setSelectedCard(res[0]?.id);
    }
    setCardList(res);
    progressiveWithdraw.getState().setAccounts(res);
  };

  const getWithDrawInfo = async () => {
    const res = await getWithdrawInfo();
    setHostWithdrawInfo(res);
    progressiveWithdraw.getState().setWithdrawInfo(res);
  };

  const onGoAddBank = (
    cardInfo?: CardListItemType,
    ifscCodeError?: boolean,
  ) => {
    if (!globalStore.token) {
      goTo('Login', {backPage: 'Home'});
      return;
    }
    goTo('AddBank', {
      ifscCodeError,
      isFirst: cardList.length === 0 ? '1' : '0',
      cardInfo,
    });
  };

  const onWithdrawSubmit = async () => {
    if (!inApp) {
      const {hasClaimed} = await getRewardStatus();
      const modals: React.ReactNode[] = [];
      let id;
      if (!hasClaimed) {
        id = TO_WITH_DRAW;
        modals.push(<ToWithdraw />);
      } else {
        id = DOWNLOAD_REWARDED;
        modals.push(<Rewarded />);
      }
      showModal({
        id,
        modals,
      });
      return;
    }

    try {
      const validation = validateWithdrawSubmit({
        isLoggedIn: progressiveSession.getState().ensureSession().ok,
        payType,
        amount: price,
        withdrawableBalance: effectiveWithdrawInfo?.withdrawableBalance,
        selectedAccountId: selectedCard,
        selectedCoin,
        recipientAddress,
        payConfig,
      });

      if (!validation.ok) {
        if (validation.issue === 'needLogin') {
          goTo('Login', {backPage: 'Home'});
          return;
        }
        const key = validation.issue
          ? WITHDRAW_SUBMIT_MESSAGE_KEY[validation.issue]
          : '';
        if (key) {
          showProgressiveHostToast({
            type: 'warning',
            message: i18n.t(key, validation.params),
          });
        }
        return;
      }

      setLoading(true);
      if (payType === 'UPI') {
        await onWithdraw({
          cardId: selectedCard as string,
          price: Number(price),
        });
      } else {
        await postCryptoWithdrawal({
          payAddress: recipientAddress,
          amount: Number(price),
          payCurrency: (selectedCoin as CryptoCoin).code,
        });
      }

      globalStore.updateAmount.next({});
      const received = calculateWithdrawReceived(
        price,
        effectiveWithdrawInfo?.withdrawFeeRate || 0,
      );
      goTo('WithdrawSuccess', {amount: received});
    } catch (e: any) {
      if (e.data?.code === 9601) {
        const card = cardList.find(item => item.id === selectedCard);
        onGoAddBank(card, true);
      }
    } finally {
      setLoading(false);
    }
  };

  const amountOptions = React.useMemo(
    () =>
      rawAmountOptions.filter(
        (option): option is number | 'Max' =>
          typeof option === 'number' || option === 'Max',
      ),
    [rawAmountOptions],
  );

  const handleGotoRecords = () => {
    if (!globalStore.token) {
      goTo('Login', {backPage: 'Home'});
      return;
    }
    goTo('WithdrawRecords');
  };

  return (
    <div className="overflow-y-auto" style={viewportHeightStyle}>
      <WithdrawWebHomePage
        payType={payType}
        amount={price}
        withdrawInfo={effectiveWithdrawInfo}
        accounts={cardList}
        selectedAccountId={selectedCard}
        amountOptions={amountOptions}
        cryptoCoins={cryptoCoinList}
        selectedCoinCode={selectedCoin?.code}
        recipientAddress={recipientAddress}
        payConfig={payConfig}
        loading={loading || amountOptionsLoading}
        currencySymbol={globalStore.currency}
        addBankIconColor={theme.basicColor.primary}
        formatAmount={value =>
          toPriceStr(Number(value || 0), {
            thousands: true,
            spacing: true,
            fixed: 2,
            currency: globalStore.currency,
          })
        }
        onBack={goBack}
        onCustomerService={goCS}
        onRecords={handleGotoRecords}
        onPayTypeChange={setPayType}
        onAmountChange={setPrice}
        onAccountSelect={() => setShowCard(true)}
        onAddBank={() => onGoAddBank()}
        accountPopupVisible={showCard}
        onAccountPopupClose={() => setShowCard(false)}
        onAccountChange={cardId => setSelectedCard(cardId)}
        onAccountEdit={card => {
          setShowCard(false);
          onGoAddBank(card as CardListItemType);
        }}
        accountPopupCloseOnSelect={false}
        onCryptoCoinSelect={() => setShowCoinSelector(true)}
        cryptoCoinPopupVisible={showCoinSelector}
        onCryptoCoinPopupClose={() => setShowCoinSelector(false)}
        onCryptoCoinChange={coin => {
          setSelectedCoin(coin as CryptoCoin);
          setShowCoinSelector(false);
        }}
        onRecipientAddressChange={setRecipientAddress}
        onSubmit={onWithdrawSubmit}
      />
    </div>
  );
};

export default Withdraw;
